import type { TiledObjectMetadata } from "./tiled.js";

/** Cardinal directions expressed as radians. */
export enum Direction {
  North = 0,
  East = Math.PI / 2,
  South = Math.PI,
  West = (3 * Math.PI) / 2,
}

/** A logical 2-D coordinate on the dungeon grid. */
export interface Position {
  readonly x: number;
  readonly z: number;
}

/**
 * Edge flags for a single grid cell.
 * Each flag is true when a wall exists on that face (razor-wall model).
 */
export interface CellEdges {
  north: boolean;
  east: boolean;
  south: boolean;
  west: boolean;
}

/** All data the engine tracks for a single grid cell. */
export interface GridCell {
  readonly position: Position;
  edges: CellEdges;
  /** Solid walls occupy the cell itself (snake-tunnel fallback). */
  solid: boolean;
  /**
   * Outdoor flag. When true the DungeonBuilder skips the ceiling quad and
   * routes floor + wall meshes to the outdoor material variants so an
   * overhead sunlight + sky background can show through. Painted in
   * Tiled via an optional `no_ceiling` tilelayer (any GID > 0 marks the
   * cell as outdoor). Optional so unit tests can build a cell without
   * opting into the outdoor pipeline — treated as `false` when absent.
   */
  noCeiling?: boolean;
  /** Tiled object-layer metadata attached at load time. */
  metadata?: TiledObjectMetadata;
  /**
   * Per-cell texture set override (T03/T04). Populated at load time
   * when a `zones` object-layer rect (T03) or a per-cell marker /
   * tile-property (T04) claims the cell. `DungeonBuilder` resolves
   * the final set as `cell.textureSet ?? map.textureSet ?? undefined`
   * — i.e. cell wins over zone wins over map wins over adventure
   * default.
   */
  textureSet?: string;
}

/** The full logical dungeon map loaded from a Tiled .tmj export. */
export interface DungeonMap {
  readonly id: string;
  readonly width: number;
  readonly height: number;
  /** Row-major flat array: index = z * width + x */
  readonly cells: readonly GridCell[];
  readonly playerStart: Position;
  readonly playerStartFacing: Direction;
  /** Human-readable floor name from the Tiled `display_name` map property (W13). */
  readonly displayName?: string;
  /**
   * Named texture set override (T02). When set, dungeon surfaces resolve
   * to `<adventure>/world/textures/sets/<textureSet>/<surface>/…`
   * instead of the adventure default. Surfaces missing from the set fall
   * through to the adventure default (not to the base tint).
   */
  readonly textureSet?: string;
}
